/**
 * Billing.tsx — customer billing center, Glass + Aurora language.
 *
 * Sections:
 *   1. KPI row — monthly spend (w/ 12-month paid sparkline), active
 *      subscriptions, next charge, outstanding balance
 *   2. Payment methods (derived from subscriptions) + upcoming charges
 *   3. Subscriptions — cancel (confirm modal) / resume
 *   4. Invoices — status/year/search filters, pagination, CSV export
 *
 * Data: /api/billing/subscriptions + /api/invoices. Payment-method
 * management goes through the Stripe Billing Portal via
 * POST /api/billing/portal-session.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashShell from '../../components/dash/DashShell';
import GlassCard from '../../components/dash/GlassCard';
import Pill, { pillForStatus } from '../../components/dash/Pill';

interface Subscription {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product_name: string | null;
  metadata: Record<string, string>;
  payment_method: { brand?: string; last4?: string; exp_month?: number; exp_year?: number } | null;
  created_at?: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  amount: string | number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_date: string | null;
  created_at: string;
}

const apiBase = process.env.REACT_APP_API_URL || '';
const PAGE_SIZE = 10;

function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  return fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}

function fmt(d?: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function monthlyEquivalent(s: Subscription): number {
  const perPeriod = s.amount / (s.interval_count || 1);
  if (s.interval === 'year') return perPeriod / 12;
  if (s.interval === 'week') return perPeriod * (52 / 12);
  if (s.interval === 'day') return perPeriod * 30;
  return perPeriod;
}

const Billing: React.FC = () => {
  const { user } = useAuth();
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<Subscription | null>(null);

  // Invoice toolbar state
  const [invStatus, setInvStatus] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [invYear, setInvYear] = useState<'all' | number>('all');
  const [invQuery, setInvQuery] = useState('');
  const [invPage, setInvPage] = useState(0);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [sRes, iRes] = await Promise.all([
        authFetch('/api/billing/subscriptions').then(r => r.json()),
        authFetch('/api/invoices').then(r => r.json()),
      ]);
      if (sRes.success) setSubs(sRes.subscriptions || []);
      else setErr(sRes.error || 'Failed to load subscriptions');
      if (iRes.success) setInvoices(iRes.data || iRes.invoices || []);
    } catch (e: any) { setErr(e.message || 'Network error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  // ---------------------------------------------------------------- derived
  const activeSubs = useMemo(() => subs.filter(s => ['active', 'trialing', 'past_due'].includes(s.status)), [subs]);
  const cancelingCount = useMemo(() => activeSubs.filter(s => s.cancel_at_period_end).length, [activeSubs]);
  const monthlyTotal = useMemo(
    () => activeSubs.filter(s => !s.cancel_at_period_end).reduce((sum, s) => sum + monthlyEquivalent(s), 0),
    [activeSubs],
  );

  const upcoming = useMemo(() => {
    const now = Date.now();
    return activeSubs
      .filter(s => !s.cancel_at_period_end && s.current_period_end && new Date(s.current_period_end).getTime() > now)
      .map(s => ({ sub: s, at: new Date(s.current_period_end as string) }))
      .sort((a, b) => a.at.getTime() - b.at.getTime());
  }, [activeSubs]);

  const nextCharge = upcoming[0] || null;

  const unpaid = useMemo(() => {
    const rows = invoices.filter(i => ['sent', 'overdue'].includes((i.status || '').toLowerCase()));
    return { count: rows.length, total: rows.reduce((s, i) => s + Number(i.amount || 0), 0) };
  }, [invoices]);

  // 12-month paid-invoice totals for the spend sparkline (oldest → newest).
  const spendSpark = useMemo(() => {
    const buckets: number[] = new Array(12).fill(0);
    const now = new Date();
    for (const inv of invoices) {
      if ((inv.status || '').toLowerCase() !== 'paid') continue;
      const d = new Date(inv.paid_date || inv.created_at);
      const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      if (monthsAgo >= 0 && monthsAgo < 12) buckets[11 - monthsAgo] += Number(inv.amount || 0);
    }
    return buckets;
  }, [invoices]);

  // Unique cards across subscriptions, with which/how many subs each covers.
  const paymentMethods = useMemo(() => {
    const map = new Map<string, { brand: string; last4: string; exp_month?: number; exp_year?: number; subCount: number }>();
    for (const s of activeSubs) {
      const pm = s.payment_method;
      if (!pm?.last4) continue;
      const key = `${pm.brand}-${pm.last4}-${pm.exp_month}-${pm.exp_year}`;
      const cur = map.get(key);
      if (cur) cur.subCount += 1;
      else map.set(key, { brand: pm.brand || 'card', last4: pm.last4, exp_month: pm.exp_month, exp_year: pm.exp_year, subCount: 1 });
    }
    return Array.from(map.values());
  }, [activeSubs]);

  const invoiceYears = useMemo(() => {
    const ys = new Set<number>();
    invoices.forEach(i => ys.add(new Date(i.created_at).getFullYear()));
    return Array.from(ys).sort((a, b) => b - a);
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const q = invQuery.trim().toLowerCase();
    return invoices.filter(i => {
      const st = (i.status || '').toLowerCase();
      if (invStatus === 'paid' && st !== 'paid') return false;
      if (invStatus === 'unpaid' && !['sent', 'overdue'].includes(st)) return false;
      if (invYear !== 'all' && new Date(i.created_at).getFullYear() !== invYear) return false;
      if (q && !i.invoice_number.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [invoices, invStatus, invYear, invQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredInvoices.length / PAGE_SIZE));
  const page = Math.min(invPage, pageCount - 1);
  const pageRows = filteredInvoices.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const filteredTotal = filteredInvoices.reduce((s, i) => s + Number(i.amount || 0), 0);

  // ---------------------------------------------------------------- actions
  const doCancel = async (sub: Subscription) => {
    setConfirmCancel(null);
    setBusy(`cancel-${sub.id}`); setErr(''); setNotice('');
    try {
      const r = await authFetch(`/api/billing/subscriptions/${sub.id}/cancel`, { method: 'POST', body: JSON.stringify({}) });
      const j = await r.json();
      if (!j.success) { setErr(j.error || 'Cancel failed'); return; }
      setNotice(j.message || 'Subscription set to cancel at period end.');
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const reactivate = async (sub: Subscription) => {
    setBusy(`react-${sub.id}`); setErr(''); setNotice('');
    try {
      const r = await authFetch(`/api/billing/subscriptions/${sub.id}/reactivate`, { method: 'POST', body: JSON.stringify({}) });
      const j = await r.json();
      if (!j.success) { setErr(j.error || 'Reactivate failed'); return; }
      setNotice('Subscription resumed — it will renew as usual.');
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const openPortal = async () => {
    setBusy('portal'); setErr('');
    try {
      const r = await authFetch('/api/billing/portal-session', { method: 'POST', body: JSON.stringify({}) });
      const j = await r.json();
      if (j.success && j.url) { window.location.href = j.url; return; }
      setErr(j.error || 'Could not open the billing portal');
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const exportCsv = () => {
    const head = 'invoice_number,date,amount,currency,status,due_date,paid_date';
    const lines = filteredInvoices.map(i =>
      [i.invoice_number, fmt(i.created_at), Number(i.amount).toFixed(2), i.currency, i.status, fmt(i.due_date), fmt(i.paid_date)]
        .map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[head, ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `12and22-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const isCardExpiringSoon = (m?: number, y?: number) => {
    if (!m || !y) return false;
    const expiry = new Date(y, m, 1); // first day AFTER the expiry month
    return expiry.getTime() - Date.now() < 60 * 24 * 3600 * 1000;
  };

  // ---------------------------------------------------------------- render
  return (
    <DashShell
      title="Billing"
      subtitle="Subscriptions, payment methods, upcoming charges and invoices."
      right={
        <button
          onClick={openPortal}
          disabled={busy === 'portal'}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 transition"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /></svg>
          {busy === 'portal' ? 'Opening…' : 'Manage payment method'}
        </button>
      }
    >
      {err && <GlassCard delay={40} tint="rose" className="px-4 py-3 text-sm text-rose-700 mb-6">{err}</GlassCard>}
      {notice && <GlassCard delay={40} tint="emerald" className="px-4 py-3 text-sm text-emerald-700 mb-6">{notice}</GlassCard>}

      {/* KPI row */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatTile
          delay={80}
          label="Monthly spend"
          value={`$${monthlyTotal.toFixed(2)}`}
          sub="normalized across billing cycles"
          spark={spendSpark}
          loading={loading}
        />
        <StatTile
          delay={120}
          label="Active subscriptions"
          value={String(activeSubs.length)}
          sub={cancelingCount > 0 ? `${cancelingCount} ending at period close` : 'all renewing'}
          loading={loading}
        />
        <StatTile
          delay={160}
          label="Next charge"
          value={nextCharge ? `$${nextCharge.sub.amount.toFixed(2)}` : '—'}
          sub={nextCharge ? `${fmt(nextCharge.sub.current_period_end)} · ${nextCharge.sub.product_name || nextCharge.sub.metadata.service_type || 'subscription'}` : 'nothing scheduled'}
          loading={loading}
        />
        <StatTile
          delay={200}
          label="Outstanding"
          value={unpaid.count > 0 ? `$${unpaid.total.toFixed(2)}` : '$0.00'}
          sub={unpaid.count > 0 ? `${unpaid.count} unpaid invoice${unpaid.count > 1 ? 's' : ''}` : 'no open invoices'}
          tone={unpaid.count > 0 ? 'amber' : undefined}
          loading={loading}
        />
      </section>

      {/* Payment methods + upcoming charges */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 px-1">Payment methods</h2>
          <GlassCard delay={240} className="p-4 flex-1">
            {loading ? <SkeletonLines n={2} /> : paymentMethods.length === 0 ? (
              <div className="text-sm text-slate-500 p-2">No card on file yet — one is saved automatically with your first purchase.</div>
            ) : (
              <ul className="space-y-3">
                {paymentMethods.map(pm => {
                  const expiring = isCardExpiringSoon(pm.exp_month, pm.exp_year);
                  return (
                    <li key={`${pm.brand}-${pm.last4}`} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex items-center justify-center w-11 h-7 rounded-md bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wide">
                          {pm.brand.slice(0, 4)}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm text-slate-900 font-medium">•••• {pm.last4}</div>
                          <div className="text-xs text-slate-500">
                            {pm.exp_month && pm.exp_year ? `Expires ${String(pm.exp_month).padStart(2, '0')}/${pm.exp_year}` : 'expiry unknown'}
                            {' · '}{pm.subCount} subscription{pm.subCount > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      {expiring && <Pill label="expiring soon" tone="amber" />}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-4 pt-3 border-t border-slate-200/60 text-xs text-slate-500">
              Cards are stored and processed by Stripe. Use “Manage payment method” above to update or replace a card.
            </div>
          </GlassCard>
        </div>

        <div className="flex flex-col">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 px-1">Upcoming charges</h2>
          <GlassCard delay={280} className="p-4 flex-1">
            {loading ? <SkeletonLines n={3} /> : upcoming.length === 0 ? (
              <div className="text-sm text-slate-500 p-2">No renewals scheduled.</div>
            ) : (
              <ul className="divide-y divide-slate-200/60">
                {upcoming.slice(0, 5).map(({ sub, at }) => (
                  <li key={sub.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-900 font-medium truncate">
                        {sub.product_name || sub.metadata.service_type || 'Subscription'}
                      </div>
                      <div className="text-xs text-slate-500">{fmt(sub.current_period_end)} · auto-renews {sub.interval_count > 1 ? `every ${sub.interval_count} ${sub.interval}s` : `${sub.interval}ly`}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                      ${sub.amount.toFixed(2)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>
        </div>
      </section>

      {/* Subscriptions — service cards with renewal progress */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 px-1">Subscriptions</h2>
        {loading && subs.length === 0 ? (
          <GlassCard delay={320} className="p-4"><SkeletonLines n={3} /></GlassCard>
        ) : subs.length === 0 ? (
          <GlassCard delay={320} className="p-6 text-sm text-slate-500 text-center">No subscriptions yet.</GlassCard>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {subs.map((s, idx) => {
              const kind = serviceKind(s);
              const ending = s.cancel_at_period_end;
              const tonePill = ending ? { label: 'ending', tone: 'amber' as const } : pillForStatus(s.status);
              const remaining = daysLeft(s.current_period_end);
              return (
                <GlassCard key={s.id} delay={320 + idx * 40} tint={ending ? 'amber' : undefined} className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <ServiceGlyph kind={kind} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">
                          {s.product_name || KIND_LABEL[kind]}
                        </div>
                        <div className="text-xs text-slate-500 capitalize">{KIND_LABEL[kind]}</div>
                      </div>
                    </div>
                    <Pill label={tonePill.label} tone={tonePill.tone} />
                  </div>

                  <div className="mt-4 flex items-baseline gap-1.5">
                    <span className="text-2xl font-semibold text-slate-900">${s.amount.toFixed(2)}</span>
                    <span className="text-sm text-slate-500">/ {s.interval_count > 1 ? `${s.interval_count} ${s.interval}s` : s.interval}</span>
                  </div>

                  <PeriodMeter
                    start={s.current_period_start}
                    end={s.current_period_end}
                    ending={ending}
                  />
                  <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      {ending
                        ? `Ends ${fmt(s.current_period_end)} — no further charges`
                        : `Renews ${fmt(s.current_period_end)}`}
                    </span>
                    {remaining !== null && (
                      <span className="tabular-nums">{remaining <= 0 ? 'today' : `in ${remaining}d`}</span>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center justify-between gap-3">
                    {s.payment_method ? (
                      <span className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <span className="inline-flex items-center justify-center w-8 h-5 rounded bg-slate-900 text-white text-[9px] font-semibold uppercase">
                          {(s.payment_method.brand || 'card').slice(0, 4)}
                        </span>
                        <span className="font-mono">•••• {s.payment_method.last4}</span>
                      </span>
                    ) : <span className="text-xs text-slate-400">No card linked</span>}
                    {ending ? (
                      <button onClick={() => reactivate(s)} disabled={busy === `react-${s.id}`}
                              className="text-sm bg-gradient-to-b from-emerald-500 to-emerald-600 text-white px-3.5 py-1.5 rounded-lg hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-60 shadow-sm transition">
                        {busy === `react-${s.id}` ? '…' : 'Resume'}
                      </button>
                    ) : s.status === 'active' || s.status === 'trialing' ? (
                      <button onClick={() => setConfirmCancel(s)} disabled={busy === `cancel-${s.id}`}
                              className="text-sm text-slate-500 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200 px-3.5 py-1.5 rounded-lg disabled:opacity-60 transition">
                        {busy === `cancel-${s.id}` ? '…' : 'Cancel'}
                      </button>
                    ) : null}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </section>

      {/* Invoices */}
      <section>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 px-1">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Invoices</h2>
          <div className="flex flex-wrap items-center gap-2">
            {(['all', 'paid', 'unpaid'] as const).map(k => (
              <button key={k}
                onClick={() => { setInvStatus(k); setInvPage(0); }}
                className={`text-xs px-3 py-1.5 rounded-lg border transition ${
                  invStatus === k
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white/70 text-slate-600 border-slate-200 hover:text-slate-900'
                }`}>
                {k === 'all' ? 'All' : k === 'paid' ? 'Paid' : 'Unpaid'}
              </button>
            ))}
            <select
              value={String(invYear)}
              onChange={e => { setInvYear(e.target.value === 'all' ? 'all' : Number(e.target.value)); setInvPage(0); }}
              className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white/70 text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-300"
              aria-label="Filter by year"
            >
              <option value="all">All years</option>
              {invoiceYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <input
              value={invQuery}
              onChange={e => { setInvQuery(e.target.value); setInvPage(0); }}
              placeholder="Search invoice #"
              className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white/70 text-slate-700 w-36 focus:outline-none focus:ring-2 focus:ring-orange-300"
              aria-label="Search invoices"
            />
            <button onClick={exportCsv} disabled={filteredInvoices.length === 0}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white/70 text-slate-600 hover:text-slate-900 disabled:opacity-50 transition">
              Export CSV
            </button>
          </div>
        </div>

        <GlassCard delay={360}>
          {loading && invoices.length === 0 ? (
            <div className="p-6"><SkeletonLines n={4} /></div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-sm text-slate-500 p-6 text-center">
              {invoices.length === 0 ? 'No invoices yet.' : 'No invoices match these filters.'}
            </div>
          ) : (
            <>
              {/* Desktop ledger — month-grouped */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200/60">
                      <th className="py-3 px-5">Invoice</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-3">Paid / due</th>
                      <th className="py-3 px-3 text-right">Amount</th>
                      <th className="py-3 px-5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByMonth(pageRows).map(group => (
                      <React.Fragment key={group.key}>
                        <tr>
                          <td colSpan={5} className="pt-4 pb-1.5 px-5">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{group.label}</span>
                            <span className="text-[10px] text-slate-400 ml-2 tabular-nums">
                              ${group.total.toFixed(2)}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map(i => {
                          const st = (i.status || '').toLowerCase();
                          const p = pillForStatus(i.status);
                          return (
                            <tr key={i.id} className="group border-b border-slate-200/40 hover:bg-slate-50/60 transition">
                              <td className="py-3 px-5">
                                <div className="font-mono text-slate-900">{i.invoice_number}</div>
                                <div className="text-xs text-slate-500">{fmt(i.created_at)}</div>
                              </td>
                              <td className="py-3 px-3"><Pill label={p.label} tone={p.tone} /></td>
                              <td className="py-3 px-3 text-slate-600">{st === 'paid' ? fmt(i.paid_date) : fmt(i.due_date)}</td>
                              <td className="py-3 px-3 text-right text-slate-900 font-semibold tabular-nums whitespace-nowrap">
                                {i.currency} {Number(i.amount).toFixed(2)}
                              </td>
                              <td className="py-3 px-5">
                                <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition">
                                  <a href={`${apiBase}/api/invoices/${i.id}/preview`} target="_blank" rel="noopener noreferrer"
                                     title="View invoice" aria-label={`View invoice ${i.invoice_number}`}
                                     className="p-1.5 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-50 transition">
                                    <IconEye />
                                  </a>
                                  <a href={`${apiBase}/api/invoices/${i.id}/download`}
                                     title="Download invoice" aria-label={`Download invoice ${i.invoice_number}`}
                                     className="p-1.5 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-50 transition">
                                    <IconDownload />
                                  </a>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile — stacked cards */}
              <ul className="md:hidden divide-y divide-slate-200/60">
                {pageRows.map(i => {
                  const st = (i.status || '').toLowerCase();
                  const p = pillForStatus(i.status);
                  return (
                    <li key={i.id} className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-sm text-slate-900">{i.invoice_number}</span>
                        <Pill label={p.label} tone={p.tone} />
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">
                          {fmt(i.created_at)} · {st === 'paid' ? `paid ${fmt(i.paid_date)}` : `due ${fmt(i.due_date)}`}
                        </span>
                        <span className="text-sm font-semibold text-slate-900 tabular-nums">{i.currency} {Number(i.amount).toFixed(2)}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-4">
                        <a href={`${apiBase}/api/invoices/${i.id}/preview`} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-slate-700 hover:text-orange-600 transition">View</a>
                        <a href={`${apiBase}/api/invoices/${i.id}/download`}
                           className="text-xs text-slate-700 hover:text-orange-600 transition">Download</a>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 text-xs text-slate-500">
                <div>
                  {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                  {' · total '}<span className="text-slate-900 font-semibold tabular-nums">${filteredTotal.toFixed(2)}</span>
                </div>
                {pageCount > 1 && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setInvPage(Math.max(0, page - 1))} disabled={page === 0}
                            className="px-2.5 py-1 rounded-md border border-slate-200 bg-white/70 disabled:opacity-40 hover:text-slate-900 transition">‹</button>
                    <span className="tabular-nums">{page + 1} / {pageCount}</span>
                    <button onClick={() => setInvPage(Math.min(pageCount - 1, page + 1))} disabled={page >= pageCount - 1}
                            className="px-2.5 py-1 rounded-md border border-slate-200 bg-white/70 disabled:opacity-40 hover:text-slate-900 transition">›</button>
                  </div>
                )}
              </div>
            </>
          )}
        </GlassCard>
      </section>

      {/* Cancel confirmation modal */}
      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button aria-label="Close" onClick={() => setConfirmCancel(null)}
                  className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6 animate-[fadeUp_.25s_ease]">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Cancel {confirmCancel.product_name || 'this subscription'}?
            </h3>
            <p className="text-sm text-slate-600 mb-1">
              Your service stays fully active until <strong>{fmt(confirmCancel.current_period_end)}</strong> — everything you've paid for, you keep.
            </p>
            <p className="text-sm text-slate-600 mb-5">
              After that date it ends and no further charges are made. You can resume any time before then.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmCancel(null)}
                      className="px-4 py-2 rounded-xl text-sm text-slate-600 hover:text-slate-900 transition">
                Keep subscription
              </button>
              <button onClick={() => doCancel(confirmCancel)}
                      className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition">
                Cancel at period end
              </button>
            </div>
          </div>
        </div>
      )}
    </DashShell>
  );
};

// ============================================================================
// Pieces
// ============================================================================

/** KPI tile: sentence-case label, semibold value, optional context line and
 *  12-month sparkline. Value uses proportional figures (no tabular-nums —
 *  reserved for table columns). */
const StatTile: React.FC<{
  label: string;
  value: string;
  sub?: string;
  spark?: number[];
  tone?: 'amber';
  delay?: number;
  loading?: boolean;
}> = ({ label, value, sub, spark, tone, delay, loading }) => (
  <GlassCard delay={delay} tint={tone} className="p-4">
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    {loading ? (
      <div className="h-8 w-24 rounded-md bg-slate-200/70 animate-pulse" />
    ) : (
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
    )}
    <div className="flex items-end justify-between gap-2 mt-1 min-h-[1.25rem]">
      {sub && !loading && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      {spark && !loading && <Sparkline values={spark} />}
    </div>
  </GlassCard>
);

/** 12-point single-series sparkline: de-emphasized line, current month as an
 *  accent dot. No legend needed — the tile label names the series. */
const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  const W = 96, H = 26, PAD = 3;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => ({
    x: PAD + (i * (W - 2 * PAD)) / Math.max(1, values.length - 1),
    y: H - PAD - (v / max) * (H - 2 * PAD),
  }));
  const dPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1];
  const monthName = (offset: number) => {
    const d = new Date(); d.setMonth(d.getMonth() - (values.length - 1 - offset));
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };
  return (
    <svg width={W} height={H} className="flex-shrink-0" role="img"
         aria-label={`Paid invoices over the last 12 months, latest $${values[values.length - 1].toFixed(0)}`}>
      <title>{values.map((v, i) => `${monthName(i)}: $${v.toFixed(2)}`).join(' · ')}</title>
      <path d={dPath} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="2.5" fill="#feb44b" stroke="#fff" strokeWidth="1" />
    </svg>
  );
};

const SkeletonLines: React.FC<{ n: number }> = ({ n }) => (
  <div className="space-y-3">
    {Array.from({ length: n }, (_, i) => (
      <div key={i} className="h-4 rounded-md bg-slate-200/70 animate-pulse" style={{ width: `${85 - i * 12}%` }} />
    ))}
  </div>
);

// -------------------------------------------------------------- subscriptions

type ServiceKindKey = 'vps' | 'hosting' | 'email' | 'domain' | 'other';

const KIND_LABEL: Record<ServiceKindKey, string> = {
  vps: 'VPS server',
  hosting: 'Web hosting',
  email: 'Business email',
  domain: 'Domain',
  other: 'Subscription',
};

function serviceKind(s: Subscription): ServiceKindKey {
  const hay = `${s.metadata?.service_type || ''} ${s.product_name || ''}`.toLowerCase();
  if (hay.includes('vps')) return 'vps';
  if (hay.includes('email') || hay.includes('mail')) return 'email';
  if (hay.includes('domain')) return 'domain';
  if (hay.includes('host')) return 'hosting';
  return 'other';
}

/** Tinted icon square identifying the service type. */
const ServiceGlyph: React.FC<{ kind: ServiceKindKey }> = ({ kind }) => {
  const tintCls: Record<ServiceKindKey, string> = {
    vps: 'bg-sky-50 text-sky-700 border-sky-200',
    hosting: 'bg-violet-50 text-violet-700 border-violet-200',
    email: 'bg-teal-50 text-teal-700 border-teal-200',
    domain: 'bg-amber-50 text-amber-700 border-amber-200',
    other: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl border flex-shrink-0 ${tintCls[kind]}`} aria-hidden>
      {kind === 'vps' && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 8h10M7 12h10M7 16h6" />
        </svg>
      )}
      {kind === 'hosting' && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" />
          <circle cx="7" cy="7" r="0.8" fill="currentColor" /><circle cx="7" cy="17" r="0.8" fill="currentColor" />
        </svg>
      )}
      {kind === 'email' && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
        </svg>
      )}
      {kind === 'domain' && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
      )}
      {kind === 'other' && (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" />
        </svg>
      )}
    </span>
  );
};

function daysLeft(end: string | null): number | null {
  if (!end) return null;
  return Math.max(0, Math.ceil((new Date(end).getTime() - Date.now()) / (24 * 3600 * 1000)));
}

/** Thin billing-period meter: elapsed share of the current period. The
 *  unfilled track is a lighter step of the same neutral ramp; the fill is
 *  the brand accent (amber when the subscription is ending, so state reads
 *  across the whole bar). */
const PeriodMeter: React.FC<{ start: string | null; end: string | null; ending: boolean }> = ({ start, end, ending }) => {
  if (!start || !end) return null;
  const s = new Date(start).getTime(), e = new Date(end).getTime();
  const pct = e > s ? Math.min(100, Math.max(0, ((Date.now() - s) / (e - s)) * 100)) : 0;
  return (
    <div className="mt-3 h-1.5 rounded-full bg-slate-200/80 overflow-hidden"
         role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}
         aria-label={ending ? 'Time until service ends' : 'Billing period progress'}>
      <div
        className={`h-full rounded-full transition-all ${ending ? 'bg-amber-400' : 'bg-gradient-to-r from-[#feb44b] to-[#f39c12]'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// ------------------------------------------------------------------ invoices

function groupByMonth(rows: Invoice[]): { key: string; label: string; total: number; rows: Invoice[] }[] {
  const groups: { key: string; label: string; total: number; rows: Invoice[] }[] = [];
  for (const inv of rows) {
    const d = new Date(inv.created_at);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) {
      g = { key, label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), total: 0, rows: [] };
      groups.push(g);
    }
    g.rows.push(inv);
    g.total += Number(inv.amount || 0);
  }
  return groups;
}

const IconEye: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const IconDownload: React.FC = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12m0 0 4-4m-4 4-4-4" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

export default Billing;
